/**
 * Alta/edición de cuentas bancarias del complemento de Pagos 2.0.
 *
 * Aquí vive SOLO la parte pura: a partir del estado del formulario arma el
 * payload que se le manda a v2/documentos/{inserta,actualiza}_banco_* y decide
 * si falta algo. El DOM y el AJAX se quedan en la vista
 * (noble/documentos/listado.php), que es quien llama a esto.
 *
 * El dato que más se olvida es el RFC: NO es el RFC del cliente ni el del
 * emisor, es el del BANCO — el atributo RfcEmisorCtaOrd / RfcEmisorCtaBen del
 * XML, "la clave del RFC de la entidad emisora de la cuenta". Sale del catálogo
 * de bancos (catsat_bancos), viaja en el value/data-rfc de cada <option> y el
 * servidor lo exige. Cuando el catálogo se sirvió sin RFC, el alta murió con
 * "El campo rfc es obligatorio"; por eso se valida aquí, con un mensaje que
 * dice qué hacer, antes de gastar un viaje al servidor.
 */

/** Patrón del XSD Pagos 2.0 para CtaOrdenante/CtaBeneficiario, acotado a 30 por la BD. */
var RE_CUENTA = /^[A-Z0-9_]{10,30}$/;
/** Lo que el servidor acepta en `rfc`: min_length[8]|max_length[15]. */
var RFC_MIN = 8;
var RFC_MAX = 15;

/** Normaliza el número de cuenta como lo guarda el servidor: mayúsculas, sin espacios ni guiones. */
export function normalizarCuenta(valor) {
  return String(valor == null ? '' : valor).toUpperCase().replace(/[\s\-]/g, '');
}

/** Normaliza un RFC: mayúsculas y sin espacios alrededor. */
export function normalizarRfc(valor) {
  return String(valor == null ? '' : valor).trim().toUpperCase();
}

/**
 * Arma el payload del alta/edición de una cuenta bancaria.
 *
 * @param {Object} estado
 * @param {'ordenante'|'beneficiaria'} estado.tipo
 * @param {string|number} [estado.editId]      id de la cuenta cuando se edita; vacío al dar de alta
 * @param {string|number} [estado.receptorId]  id del cliente; obligatorio solo para la ordenante
 * @param {string} estado.bancoRfc             RFC del banco (value/data-rfc del <option>)
 * @param {string} estado.bancoNombre          nombre corto del banco (data-nombre del <option>)
 * @param {string} estado.numCuenta            número de cuenta tal cual lo tecleó el usuario
 * @returns {{ok: true, editando: boolean, payload: Object}|{ok: false, campo: string, error: string}}
 */
export function armarPayloadCuentaBancaria(estado) {
  var e = estado || {};
  var esOrdenante = e.tipo !== 'beneficiaria';

  var editId = (e.editId === 0 || e.editId) ? String(e.editId) : '';
  var receptorId = (e.receptorId === 0 || e.receptorId) ? String(e.receptorId) : '';
  var nombre = String(e.bancoNombre == null ? '' : e.bancoNombre).trim();
  var rfc = normalizarRfc(e.bancoRfc);
  var cuenta = normalizarCuenta(e.numCuenta);

  if (esOrdenante && !editId && receptorId === '') {
    return {
      ok: false,
      campo: 'receptor_id',
      error: 'Primero hay que tener al cliente en el catálogo para poder guardarle una cuenta.',
    };
  }

  if (nombre === '') {
    return {
      ok: false,
      campo: 'nombre',
      error: esOrdenante
        ? 'Selecciona el banco origen (el banco de tu cliente).'
        : 'Selecciona el banco beneficiario (donde reside tu cuenta).',
    };
  }

  // El RFC del banco no lo captura el usuario: viene del catálogo. Si falta, el
  // problema es el catálogo, y decirlo así evita que alguien busque el error en
  // los datos del cliente.
  if (rfc === '') {
    return {
      ok: false,
      campo: 'rfc',
      error: 'El banco «' + nombre + '» no trae RFC en el catálogo, y el SAT lo exige para la cuenta '
        + (esOrdenante ? 'ordenante' : 'beneficiaria')
        + '. Elige otro banco o escríbenos a soporte@facturadigital.com.mx.',
    };
  }
  if (rfc.length < RFC_MIN || rfc.length > RFC_MAX) {
    return {
      ok: false,
      campo: 'rfc',
      error: 'El RFC del banco «' + nombre + '» no tiene la forma esperada en el catálogo (' + rfc + '). Escríbenos a soporte@facturadigital.com.mx y lo revisamos.',
    };
  }

  if (!RE_CUENTA.test(cuenta)) {
    return {
      ok: false,
      campo: 'num_cuenta',
      error: 'La cuenta debe tener de 10 a 30 caracteres (letras, números o guión bajo), como marca el estándar del SAT.',
    };
  }

  var payload = {
    id: editId,
    nombre: nombre,
    rfc: rfc,
    num_cuenta: cuenta,
  };
  if (esOrdenante) {
    payload.receptor_id = receptorId;
  }

  return { ok: true, editando: editId !== '', payload: payload };
}

export default { armarPayloadCuentaBancaria: armarPayloadCuentaBancaria, normalizarCuenta: normalizarCuenta, normalizarRfc: normalizarRfc };
